import React, { useEffect, useMemo, useState } from 'react'
import { confirmation_modal, downLoadFile, fetchApiData } from '../fnc.js'

var moment = require('moment');

/**
 * Outlook Classic signature generator UI.
 * Backed by api=tools trigger=outlook_signature (file-based under savedFiles/signatures/).
 */

const emptyForm = {
	name: '',
	title: '',
	phone: '',
	store: 'eio',
};

const defaultStores = [
	{ key: 'eio', label: 'EIO', company: 'Electronic Inventory Online, Inc', website: 'https://www.eio.com/', defaultPhone: '310.533.5150', templateReady: true },
	{ key: 'haus', label: 'Haus of Tools', company: 'Haus of Tools, Inc', website: 'https://hausoftools.com/', defaultPhone: '310.870.3746', templateReady: true },
	{ key: 'forthetrades', label: 'For The Trades', company: 'Forthetrades NFPs', website: 'https://www.forthetrades.org', defaultPhone: '310.533.5150', templateReady: true },
];

function storeBadgeClass(storeKey) {
	if (storeKey === 'haus') return 'badge-dark';
	if (storeKey === 'forthetrades') return 'badge-warning';
	return 'badge-primary';
}

export default function OutlookSignature(props) {
	if (!props.securityClearance(props)) return null;

	const [rows, setRows] = useState([]);
	const [stores, setStores] = useState(defaultStores);
	const [summary, setSummary] = useState({ total: 0, storeCounts: {} });
	const [storeFilter, setStoreFilter] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const [selected, setSelected] = useState({});
	const [form, setForm] = useState(emptyForm);
	const [previewHtml, setPreviewHtml] = useState('');
	const [suggestedFileName, setSuggestedFileName] = useState('');
	const [overwrite, setOverwrite] = useState(false);
	const [listPreview, setListPreview] = useState(null);

	useEffect(() => {
		loadList();
	}, [storeFilter]);

	useEffect(() => {
		const timer = setTimeout(() => {
			if (form.name.trim() && form.title.trim() && form.phone.trim() && form.store) {
				loadLivePreview();
			} else {
				setPreviewHtml('');
				setSuggestedFileName('');
			}
		}, 350);
		return () => clearTimeout(timer);
	}, [form.name, form.title, form.phone, form.store]);

	const loadList = () => {
		props.loading(true);
		fetchApiData('tools', 'outlook_signature', {
			method: 'list',
			store: storeFilter || undefined,
		}, null, (response) => {
			props.loading(false);

			if (!response || !response.success) {
				setRows([]);
				setSummary({ total: 0, storeCounts: {} });
				props.info({ error: response && response.error ? response.error : 'Unable to load signatures.' });
				return;
			}

			setRows(Array.isArray(response.data?.rows) ? response.data.rows : []);
			setStores(Array.isArray(response.data?.stores) && response.data.stores.length ? response.data.stores : defaultStores);
			setSummary(response.summary || { total: 0, storeCounts: {} });
			setSelected({});
		});
	}

	const loadLivePreview = () => {
		fetchApiData('tools', 'outlook_signature', {
			method: 'preview',
			name: form.name.trim(),
			title: form.title.trim(),
			phone: form.phone.trim(),
			store: form.store,
		}, null, (response) => {
			if (!response || !response.success) {
				setPreviewHtml('');
				setSuggestedFileName('');
				return;
			}
			setPreviewHtml(response.data?.html || '');
			setSuggestedFileName(response.data?.suggestedFileName || '');
		});
	}

	const updateForm = (key, value) => {
		setForm((prev) => {
			const next = { ...prev, [key]: value };
			// When switching store, offer that store's default phone if the field is empty or still the previous default.
			if (key === 'store') {
				const nextStore = stores.find((s) => s.key === value);
				const prevStore = stores.find((s) => s.key === prev.store);
				if (nextStore && (!prev.phone.trim() || (prevStore && prev.phone === prevStore.defaultPhone))) {
					next.phone = nextStore.defaultPhone || prev.phone;
				}
			}
			return next;
		});
	}

	const createSignature = () => {
		if (!form.name.trim() || !form.title.trim() || !form.phone.trim() || !form.store) {
			props.info({ error: 'Name, title, phone, and store are required.' });
			return;
		}

		props.loading(true);
		fetchApiData('tools', 'outlook_signature', {
			method: 'create',
			name: form.name.trim(),
			title: form.title.trim(),
			phone: form.phone.trim(),
			store: form.store,
			overwrite: overwrite || undefined,
		}, null, (response) => {
			props.loading(false);

			if (!response || !response.success) {
				props.info({ error: response && response.error ? response.error : 'Unable to create signature.' });
				return;
			}

			props.info({ success: `Created ${response.data?.signature?.fileName || 'signature'}.` });
			setOverwrite(false);
			loadList();
		});
	}

	const downloadFiles = (fileNames) => {
		const list = (fileNames || []).filter(Boolean);
		if (list.length === 0) {
			props.info({ error: 'Select at least one signature to download.' });
			return;
		}

		props.loading(true);
		fetchApiData('tools', 'outlook_signature', {
			method: 'download',
			fileNames: JSON.stringify(list),
			forceZip: list.length > 1 || undefined,
		}, null, (response) => {
			props.loading(false);

			if (!response || !response.success || !response.data?.base64) {
				props.info({ error: response && response.error ? response.error : 'Download failed.' });
				return;
			}

			downLoadFile(response.data.base64, response.data.fileName || 'signatures.zip', response.data.mime);
			props.info({ success: `Downloaded ${response.data.count || list.length} signature(s).` });
		});
	}

	const deleteFiles = (fileNames) => {
		const list = (fileNames || []).filter(Boolean);
		if (list.length === 0) return;

		confirmation_modal(
			props,
			() => {
				props.loading(true);
				fetchApiData('tools', 'outlook_signature', {
					method: 'delete',
					fileNames: JSON.stringify(list),
				}, null, (response) => {
					props.loading(false);
					if (!response || !response.success) {
						props.info({ error: response && response.error ? response.error : 'Delete failed.' });
						return;
					}
					props.info({ success: `Deleted ${response.data?.deleted?.length || list.length} signature(s).` });
					if (listPreview && list.includes(listPreview.fileName)) {
						setListPreview(null);
					}
					loadList();
				});
			},
			null,
			<div className="smallText font-weight-normal mt-2">
				Delete {list.length} signature file(s)? This cannot be undone.
			</div>
		);
	}

	const openListPreview = (fileName) => {
		props.loading(true);
		fetchApiData('tools', 'outlook_signature', {
			method: 'get',
			fileName,
		}, null, (response) => {
			props.loading(false);
			if (!response || !response.success) {
				props.info({ error: response && response.error ? response.error : 'Unable to preview signature.' });
				return;
			}
			setListPreview({
				fileName,
				html: response.data?.html || '',
				meta: response.data?.signature || {},
			});
		});
	}

	const fillFormFromRow = (row) => {
		setForm({
			name: row.name || '',
			title: row.title || '',
			phone: row.phone || '',
			store: row.store || 'eio',
		});
		setOverwrite(true);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	const visibleRows = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		if (!term) return rows;
		return rows.filter((row) => {
			return [row.fileName, row.name, row.title, row.phone, row.store, row.storeLabel, row.company]
				.filter((value) => value !== undefined && value !== null)
				.some((value) => String(value).toLowerCase().includes(term));
		});
	}, [rows, searchTerm]);

	const selectedFileNames = useMemo(() => {
		return Object.keys(selected).filter((fileName) => selected[fileName]);
	}, [selected]);

	const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((row) => selected[row.fileName]);

	const toggleSelectAllVisible = () => {
		if (allVisibleSelected) {
			setSelected((prev) => {
				const next = { ...prev };
				visibleRows.forEach((row) => { delete next[row.fileName]; });
				return next;
			});
			return;
		}
		setSelected((prev) => {
			const next = { ...prev };
			visibleRows.forEach((row) => { next[row.fileName] = true; });
			return next;
		});
	}

	const selectedStore = stores.find((s) => s.key === form.store);

	return <div className="container-fluid py-3">
		<div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
			<div>
				<h2 className="mb-1">Outlook Signatures</h2>
				<div className="text-muted">
					Generate Outlook Classic signatures from store templates. Files are saved under <code>savedFiles/signatures/generated/</code>.
				</div>
			</div>
			<div className="badge badge-dark p-2" title="Tool version">v0.1.0</div>
		</div>

		<div className="row mb-4">
			<div className="col-lg-3 col-md-6 mb-3">
				<div className="card border-0 shadow-sm h-100">
					<div className="card-body">
						<div className="text-muted text-uppercase small">Signatures</div>
						<div className="display-4 font-weight-bold mb-0">{summary.total || 0}</div>
					</div>
				</div>
			</div>
			{(stores.length ? stores : Object.keys(summary.storeCounts || {})).slice(0, 3).map((storeOrKey) => {
				const key = typeof storeOrKey === 'string' ? storeOrKey : storeOrKey.key;
				const label = typeof storeOrKey === 'string' ? storeOrKey : storeOrKey.label;
				const count = (summary.storeCounts && summary.storeCounts[key]) || 0;
				return <div key={key} className="col-lg-3 col-md-6 mb-3">
					<div className="card border-0 shadow-sm h-100">
						<div className="card-body">
							<div className="text-muted text-uppercase small">{label}</div>
							<div className="display-4 font-weight-bold mb-0">{count}</div>
						</div>
					</div>
				</div>;
			})}
		</div>

		<div className="row mb-4">
			<div className="col-xl-5 mb-3">
				<div className="card border-0 shadow-sm h-100">
					<div className="card-body">
						<div className="d-flex justify-content-between align-items-center mb-3">
							<h5 className="mb-0">Create Signature</h5>
							{selectedStore ? <span className={`badge ${storeBadgeClass(selectedStore.key)}`}>{selectedStore.label}</span> : null}
						</div>

						<div className="form-group">
							<label className="font-weight-bold">Store</label>
							<select
								className="form-control"
								value={form.store}
								onChange={(e) => updateForm('store', e.target.value)}
							>
								{stores.map((store) => <option key={store.key} value={store.key} disabled={!store.templateReady}>
									{store.label}{store.templateReady ? '' : ' (template missing)'}
								</option>)}
							</select>
							{selectedStore ? <small className="form-text text-muted">{selectedStore.company} · {selectedStore.website}</small> : null}
						</div>

						<div className="form-group">
							<label className="font-weight-bold">Full name</label>
							<input
								type="text"
								className="form-control"
								placeholder="Yannis Jaccard"
								value={form.name}
								onChange={(e) => updateForm('name', e.target.value)}
							/>
						</div>

						<div className="form-group">
							<label className="font-weight-bold">Title</label>
							<input
								type="text"
								className="form-control"
								placeholder="Chief Technology Officer"
								value={form.title}
								onChange={(e) => updateForm('title', e.target.value)}
							/>
						</div>

						<div className="form-group">
							<label className="font-weight-bold">Phone</label>
							<input
								type="text"
								className="form-control"
								placeholder={selectedStore?.defaultPhone || '310.533.5150'}
								value={form.phone}
								onChange={(e) => updateForm('phone', e.target.value)}
							/>
						</div>

						{suggestedFileName ? <div className="alert alert-light border small mb-3">
							<div className="text-muted mb-1">Export file name</div>
							<code>{suggestedFileName}</code>
						</div> : null}

						<div className="custom-control custom-checkbox mb-3">
							<input
								type="checkbox"
								className="custom-control-input"
								id="sigOverwrite"
								checked={overwrite}
								onChange={(e) => setOverwrite(e.target.checked)}
							/>
							<label className="custom-control-label" htmlFor="sigOverwrite">
								Overwrite if the same export name already exists
							</label>
						</div>

						<div className="d-flex flex-wrap">
							<button type="button" className="btn btn-primary mr-2 mb-2" onClick={createSignature}>
								Save signature
							</button>
							<button
								type="button"
								className="btn btn-outline-secondary mb-2"
								onClick={() => {
									setForm({
										...emptyForm,
										store: form.store,
										phone: selectedStore?.defaultPhone || '',
									});
									setOverwrite(false);
									setPreviewHtml('');
									setSuggestedFileName('');
								}}
							>
								Clear form
							</button>
						</div>

						<div className="border-top mt-3 pt-3 text-muted small text-wrap">
							Install tip: unzip into Outlook Classic&apos;s Signatures folder
							(<code>%APPDATA%\Microsoft\Signatures</code>), then pick the signature in Outlook options.
						</div>
					</div>
				</div>
			</div>

			<div className="col-xl-7 mb-3">
				<div className="card border-0 shadow-sm h-100">
					<div className="card-body">
						<div className="d-flex justify-content-between align-items-center mb-3">
							<h5 className="mb-0">Live Preview</h5>
							<span className="badge badge-light">Updates as you type</span>
						</div>
						{previewHtml ? <div
							className="border rounded p-3 bg-white"
							style={{ minHeight: '220px', overflow: 'auto' }}
							dangerouslySetInnerHTML={{ __html: previewHtml }}
						/> : <div className="text-muted border rounded p-5 text-center">
							Enter name, title, and phone to preview the selected store template.
						</div>}
					</div>
				</div>
			</div>
		</div>

		<div className="card border-0 shadow-sm mb-4">
			<div className="card-body">
				<div className="row align-items-end">
					<div className="col-lg-3 col-md-6 mb-3">
						<label className="font-weight-bold">Filter by store</label>
						<select
							className="form-control"
							value={storeFilter}
							onChange={(e) => setStoreFilter(e.target.value)}
						>
							<option value="">All stores</option>
							{stores.map((store) => <option key={store.key} value={store.key}>{store.label}</option>)}
						</select>
					</div>
					<div className="col-lg-4 col-md-6 mb-3">
						<label className="font-weight-bold">Search</label>
						<input
							type="text"
							className="form-control"
							placeholder="Name, title, phone, file..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</div>
					<div className="col-lg-5 mb-3">
						<label className="font-weight-bold d-block">Pack actions</label>
						<div className="d-flex flex-wrap">
							<button
								type="button"
								className="btn btn-success mr-2 mb-2"
								disabled={selectedFileNames.length === 0}
								onClick={() => downloadFiles(selectedFileNames)}
							>
								Download zip ({selectedFileNames.length})
							</button>
							<button
								type="button"
								className="btn btn-outline-danger mr-2 mb-2"
								disabled={selectedFileNames.length === 0}
								onClick={() => deleteFiles(selectedFileNames)}
							>
								Delete selected
							</button>
							<button type="button" className="btn btn-outline-secondary mb-2" onClick={loadList}>
								Refresh
							</button>
						</div>
					</div>
				</div>

				<div className="table-responsive">
					<table className="table table-hover align-middle mb-0">
						<thead className="thead-light">
							<tr>
								<th style={{ width: '40px' }}>
									<input
										type="checkbox"
										checked={allVisibleSelected}
										onChange={toggleSelectAllVisible}
										title="Select all visible"
									/>
								</th>
								<th>Person</th>
								<th>Store</th>
								<th>Export file</th>
								<th>Updated</th>
								<th className="text-right">Actions</th>
							</tr>
						</thead>
						<tbody>
							{visibleRows.length === 0 ? <tr>
								<td colSpan="6" className="text-center text-muted py-5">
									No signatures yet. Create one with the form above.
								</td>
							</tr> : visibleRows.map((row) => <tr key={row.fileName}>
								<td>
									<input
										type="checkbox"
										checked={!!selected[row.fileName]}
										onChange={(e) => setSelected((prev) => ({
											...prev,
											[row.fileName]: e.target.checked,
										}))}
									/>
								</td>
								<td>
									<div className="font-weight-bold">{row.name}</div>
									<div className="small text-muted">{row.title}</div>
									<div className="small">{row.phone}</div>
								</td>
								<td>
									<span className={`badge ${storeBadgeClass(row.store)} px-3 py-2`}>{row.storeLabel || row.store}</span>
								</td>
								<td style={{ minWidth: '240px' }}>
									<code className="small">{row.fileName}</code>
								</td>
								<td>
									<div>{row.updatedAt ? moment(row.updatedAt).format('YYYY-MM-DD HH:mm') : '-'}</div>
									<div className="small text-muted">{row.updatedAt ? moment(row.updatedAt).fromNow() : ''}</div>
								</td>
								<td className="text-right text-nowrap">
									<button type="button" className="btn btn-sm btn-outline-primary mr-1 mb-1" onClick={() => openListPreview(row.fileName)}>
										Preview
									</button>
									<button type="button" className="btn btn-sm btn-outline-success mr-1 mb-1" onClick={() => downloadFiles([row.fileName])}>
										Download
									</button>
									<button type="button" className="btn btn-sm btn-outline-secondary mr-1 mb-1" onClick={() => fillFormFromRow(row)}>
										Edit
									</button>
									<button type="button" className="btn btn-sm btn-outline-danger mb-1" onClick={() => deleteFiles([row.fileName])}>
										Delete
									</button>
								</td>
							</tr>)}
						</tbody>
					</table>
				</div>
			</div>
		</div>

		{listPreview ? <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.45)' }}>
			<div className="modal-dialog modal-lg modal-dialog-centered" role="document">
				<div className="modal-content">
					<div className="modal-header">
						<div>
							<h5 className="modal-title mb-0">{listPreview.meta?.name || listPreview.fileName}</h5>
							<small className="text-muted">{listPreview.fileName}</small>
						</div>
						<button type="button" className="close" onClick={() => setListPreview(null)}>
							<span>&times;</span>
						</button>
					</div>
					<div className="modal-body">
						<div
							className="border rounded p-3 bg-white"
							dangerouslySetInnerHTML={{ __html: listPreview.html }}
						/>
					</div>
					<div className="modal-footer">
						<button type="button" className="btn btn-success" onClick={() => downloadFiles([listPreview.fileName])}>
							Download
						</button>
						<button type="button" className="btn btn-secondary" onClick={() => setListPreview(null)}>
							Close
						</button>
					</div>
				</div>
			</div>
		</div> : null}
	</div>
}
